import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // TODO: Send error to logging service (e.g., Sentry, LogRocket)
    // logErrorToService(error, errorInfo);
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI - Partner Portal brand (purple/blue theme)
      return (
        <div className="min-h-screen bg-gradient-to-br from-[#282828] via-[#4c1d95] to-[#282828] flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-[#282828]/80 backdrop-blur-sm rounded-2xl border border-[#1283EB]/30 p-8 shadow-2xl">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img
                src="/aiia-logo-white.svg"
                alt="AIIA Logo"
                className="h-16"
              />
            </div>

            {/* Error Icon */}
            <div className="flex justify-center mb-4">
              <div className="bg-red-500/20 rounded-full p-3">
                <AlertCircle className="h-10 w-10 text-red-400" aria-hidden="true" />
              </div>
            </div>

            {/* Error Title */}
            <h1 className="text-2xl font-bold text-[#F8F3EC] text-center mb-2">
              Something went wrong
            </h1>

            {/* Error Message */}
            <p className="text-white/60 text-center mb-6">
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>

            {/* Error Details (Development Only) */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mb-6 bg-[#1a1a1a] rounded-lg border border-white/10 p-4">
                <summary className="text-sm font-medium text-white/80 cursor-pointer">
                  Error Details (Development Only)
                </summary>
                <div className="mt-3 space-y-2">
                  <div>
                    <p className="text-xs font-medium text-white/50 mb-1">Error:</p>
                    <pre className="text-xs text-red-400 bg-[#0a0a0a] p-2 rounded overflow-x-auto">
                      {this.state.error.toString()}
                    </pre>
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <p className="text-xs font-medium text-white/50 mb-1">Stack Trace:</p>
                      <pre className="text-xs text-white/60 bg-[#0a0a0a] p-2 rounded overflow-x-auto max-h-40 overflow-y-auto">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 mb-4">
              <button
                onClick={this.handleReset}
                className="flex-1 h-12 flex items-center justify-center gap-2 px-4 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors border border-white/10"
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="flex-1 h-12 px-4 bg-[#1283EB] hover:bg-[#1283EB]/90 text-white font-medium rounded-lg transition-colors"
              >
                Reload Page
              </button>
            </div>

            {/* Home Button */}
            <button
              onClick={() => window.location.href = '/'}
              className="w-full h-12 flex items-center justify-center gap-2 px-4 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              Return to Dashboard
            </button>

            {/* Support Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-white/50">
                If the problem persists,{' '}
                <a
                  href="mailto:support@aiia.io"
                  className="text-[#1283EB] hover:text-[#1283EB]/80 underline"
                >
                  contact support
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Functional wrapper for easier usage
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function WithErrorBoundaryWrapper(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}
